import store from '@/store';
import { ADMIN_REQUEST } from '@/store/actions/admin';
import AuthTokenStorageService from '@/services/AuthTokenStorageService';
import { ROLES } from '@/constants/roles';
import Vue from 'vue';
import i18n from '@/i18n';

export const ifNotAuthenticated = async (to, from, next) => {
  if (!store.getters.isAuthenticated) {
    next();
    return;
  }
  next('/');
};

// export const ifAuthenticated = async (to, from, next) => {
//   if (store.getters.isAuthenticated) {
//     if (!store.getters.isProfileLoaded) {
//       await store.dispatch(ADMIN_REQUEST);
//     }
//     next();
//     return;
//   }

//   next({ name: 'AdminLogin' });
//   // next();
// };

export const ifAuthenticated = async (to, from, next) => {
  if (store.getters.isProfileLoaded) {
    next();
  } else if (AuthTokenStorageService.getAccessToken()) {
    await store.dispatch(ADMIN_REQUEST);
    next();
  } else {
    next(`/login?redirect=${to.path}`);
  }
};

export const dontHavePermission = (to, from, next) => {
  Vue.toasted.show(i18n.t('toast.youDontHavePermission'), { type: 'error' });
  if (from.name) {
    next(false);
    return;
  }
  next('/');
};

/**
 * Check if user has required role(s) for accessing a route
 * @param {string|string[]} requiredRoles - Single role or array of roles
 * @returns {Function} Route guard function
 */
export const requiresRole = requiredRoles => {
  return async (to, from, next) => {
    // First check if user is authenticated
    if (!store.getters.isProfileLoaded) {
      if (AuthTokenStorageService.getAccessToken()) {
        await store.dispatch(ADMIN_REQUEST);
      } else {
        next(`/login?redirect=${to.path}`);
        return;
      }
    }

    const userRole = store.getters.me?.role;

    if (!userRole) {
      next('/login');
      return;
    }

    // Convert single role to array for easier checking
    const rolesArray = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

    if (rolesArray.includes(userRole)) {
      next();
    } else {
      dontHavePermission(to, from, next);
    }
  };
};

/**
 * Check if user has any of the specified roles
 * @param {string[]} roles - Array of roles
 * @returns {Function} Route guard function
 */
export const hasAnyRole = roles => {
  return requiresRole(roles);
};

/**
 * Check if user has all of the specified roles (for complex permissions)
 * @param {string[]} roles - Array of roles
 * @returns {Function} Route guard function
 */
export const hasAllRoles = roles => {
  return async (to, from, next) => {
    if (!store.getters.isProfileLoaded) {
      if (AuthTokenStorageService.getAccessToken()) {
        await store.dispatch(ADMIN_REQUEST);
      } else {
        next(`/login?redirect=${to.path}`);
        return;
      }
    }

    const userRole = store.getters.me?.role;

    if (!userRole) {
      next('/login');
      return;
    }

    // For this app, typically user has only one role, so this would check if user has the specific role
    if (roles.includes(userRole)) {
      next();
    } else {
      dontHavePermission(to, from, next);
    }
  };
};

// Predefined role guards for common use cases
export const requiresAdmin = requiresRole(ROLES.ADMINISTRATOR);
export const requiresChiefLoanOfficer = requiresRole(ROLES.CHIEF_LOAN_OFFICER);
export const requiresLoanOfficer = requiresRole([ROLES.LOAN_OFFICER, ROLES.CHIEF_LOAN_OFFICER]);
export const requiresAccountant = requiresRole(ROLES.ACCOUNTANT);
export const requiresOperator = requiresRole(ROLES.OPERATOR);
export const requiresStatistics = requiresRole(ROLES.STATISTICS);

// Combined role guards
export const requiredDashboardAccess = requiresRole([
  ROLES.ADMINISTRATOR,
  ROLES.STATISTICS,
  ROLES.VIEWER,
]);

export const requiresAdminAccess = requiresRole([ROLES.ADMINISTRATOR]);

export const requiresOfficersAccess = requiresRole([ROLES.ADMINISTRATOR, ROLES.CHIEF_LOAN_OFFICER]);

export const requiresCustomersAccess = requiresRole([
  ROLES.ADMINISTRATOR,
  ROLES.CHIEF_LOAN_OFFICER,
  ROLES.LOAN_OFFICER,
  ROLES.VIEWER,
  ROLES.COLLECTIONS_OFFICER,
]);

export const requiresCustomersUpdateAccess = requiresRole([
  ROLES.ADMINISTRATOR,
  ROLES.CHIEF_LOAN_OFFICER,
  ROLES.LOAN_OFFICER,
]);

export const requiresLoanAccess = requiresRole([
  ROLES.ADMINISTRATOR,
  ROLES.CHIEF_LOAN_OFFICER,
  ROLES.LOAN_OFFICER,
  ROLES.COLLECTIONS_OFFICER,
]);

export const requiresReportAccess = requiresRole([
  ROLES.ADMINISTRATOR,
  ROLES.CHIEF_LOAN_OFFICER,
  ROLES.ACCOUNTANT,
]);

export const requiresGeneralJournalAccess = requiresRole([
  ROLES.ADMINISTRATOR,
  ROLES.CHIEF_LOAN_OFFICER,
  ROLES.ACCOUNTANT,
]);

export const requiresLoanSettingsAccess = requiresRole([ROLES.ADMINISTRATOR]);
